/**
 * PostHog analytics configuration and utilities.
 *
 * Provides event tracking, user identification, and group analytics
 * for the Panora frontend application.
 */
import posthog from "posthog-js";

// Configuration from environment
const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
const POSTHOG_ENABLED = process.env.REACT_APP_POSTHOG_ENABLED === "true";

let isInitialized = false;

/**
 * Derive the deployment environment from the hostname.
 * All environments share one PostHog project; this property (matching the
 * backend's ENVIRONMENT values: "production" / "stage" / "development")
 * is how events are told apart.
 */
export const getEnvironment = () => {
  const host = window.location.hostname;
  if (host === "app.panora.cx") return "production";
  if (host === "test.panora.cx") return "stage";
  return "development";
};

/**
 * Namespace an id with the environment — all envs share one PostHog project,
 * and integer user/team PKs collide across stage/prod without the prefix.
 * Must stay in sync with the backend's posthog_client._namespaced().
 */
const namespaced = (id) => `${getEnvironment()}:${id}`;

// Super properties attached to every event. Registered at init and
// re-registered after reset() (reset clears registered properties).
const registerEnvironment = () => {
  posthog.register({ environment: getEnvironment(), surface: "web" });
};

/**
 * Initialize PostHog client.
 * Call this once at app startup.
 */
export const initPostHog = () => {
  if (isInitialized) return;

  if (!POSTHOG_ENABLED || !POSTHOG_API_KEY) {
    console.log("PostHog disabled or not configured");
    return;
  }

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      // 'history' mode auto-captures SPA route changes via pushState/replaceState
      capture_pageview: "history",
      // Capture pageleaves for session duration
      capture_pageleave: true,
      // Disable autocapture (we track interactions explicitly)
      autocapture: false,
      // Persist across sessions
      persistence: "localStorage",
      // Respect Do Not Track browser setting
      respect_dnt: true,
      // Session replay: production only, and only when the project-level
      // recording toggle is also on. maskAllText posture (decided 2026-06-12):
      // rendered text carries customer data (account names, MRR), so replay
      // captures structure/navigation/clicks but NO text or input content.
      disable_session_recording: getEnvironment() !== "production",
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
      },
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env.NODE_ENV === "development") {
          posthog.debug();
        }
      },
    });

    registerEnvironment();

    isInitialized = true;
    console.log("PostHog initialized");
  } catch (error) {
    console.error("Failed to initialize PostHog:", error);
  }
};

/**
 * Check if PostHog is enabled and initialized.
 */
export const isPostHogEnabled = () => {
  return POSTHOG_ENABLED && isInitialized;
};

/**
 * Identify a user with their properties.
 *
 * @param {string|number} userId - The user's ID
 * @param {Object} properties - User properties
 * @param {string|number} teamId - Team ID for group association
 */
export const identifyUser = (userId, properties = {}, teamId = null) => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.identify(namespaced(userId), properties);

    // Associate user with team group
    if (teamId) {
      posthog.group("team", namespaced(teamId));
    }

    if (process.env.NODE_ENV === "development") {
      console.debug("PostHog: User identified", userId);
    }
  } catch (error) {
    console.error("PostHog identify error:", error);
  }
};

/**
 * Set team/group properties.
 *
 * @param {string|number} teamId - The team's ID
 * @param {Object} properties - Team properties
 */
export const identifyTeam = (teamId, properties = {}) => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.group("team", namespaced(teamId), properties);

    if (process.env.NODE_ENV === "development") {
      console.debug("PostHog: Team identified", teamId);
    }
  } catch (error) {
    console.error("PostHog group error:", error);
  }
};

/**
 * Track a custom event.
 *
 * @param {string} eventName - Event name (e.g., "Account Viewed")
 * @param {Object} properties - Event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.capture(eventName, properties);

    if (process.env.NODE_ENV === "development") {
      console.debug("PostHog: Event tracked", eventName, properties);
    }
  } catch (error) {
    console.error("PostHog track error:", error);
  }
};

/**
 * Track a page view.
 *
 * @param {string} pageName - Page name (e.g., "Dashboard", "Customers")
 * @param {Object} properties - Additional page properties
 */
export const trackPageView = (pageName, properties = {}) => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.capture("Page Viewed", {
      page_name: pageName,
      ...properties,
    });

    if (process.env.NODE_ENV === "development") {
      console.debug("PostHog: Page view tracked", pageName);
    }
  } catch (error) {
    console.error("PostHog page view error:", error);
  }
};

/**
 * Reset the current user (on logout).
 */
export const resetUser = () => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.reset();
    // reset() wipes registered super properties — re-tag the environment
    registerEnvironment();

    if (process.env.NODE_ENV === "development") {
      console.debug("PostHog: User reset");
    }
  } catch (error) {
    console.error("PostHog reset error:", error);
  }
};

/**
 * Set a single super property (persists across events).
 *
 * @param {string} key - Property key
 * @param {any} value - Property value
 */
export const setSuperProperty = (key, value) => {
  if (!isPostHogEnabled()) return;

  try {
    posthog.register({ [key]: value });
  } catch (error) {
    console.error("PostHog register error:", error);
  }
};

export default posthog;
